import React, { useEffect, useMemo, useState } from "react";
import { getPrivateTegSetup } from "./tegSetupPrivacy";
import { listGuruPlays } from "./api/guruPlaymaker";
import GuruPlayMakerEditor from "./components/GuruPlayMakerEditor";
import GuruPlayMakerResult from "./components/GuruPlayMakerResult";
import { selectCurrentGuruPlay } from "./guruPlayLifecycle";
import { useEntitlements } from "./entitlements/EntitlementContext";

const TEG_THEME_KEY = "pwg.teg.theme";

const sourceRiskLevel = (value) => ["LOW", "MEDIUM", "HIGH"].includes(String(value || "").toUpperCase()) ? String(value).toUpperCase() : null;
const activeRiskValue = (value) => value && !["NONE", "NEUTRAL", "NO"].includes(String(value).toUpperCase());

function tegSourceSnapshot(row, model, privateSetup) {
  const flags = [];
  if (privateSetup?.status === "PREMIUM_PROTECTION") flags.push("PREMIUM_PROTECTION");
  if (activeRiskValue(row?.TEG_Expansion_Risk)) flags.push("EXPANSION_RISK");
  if (String(row?.TEG_Revision_Context || "").toUpperCase().includes("WEAK")) flags.push("WEAK_REVISIONS");
  if (activeRiskValue(row?.TEG_Exhaustion_Risk)) flags.push("EXHAUSTION_RISK");
  if (activeRiskValue(row?.TEG_Divergence)) flags.push("DIVERGENCE");
  if (activeRiskValue(row?.TEG_Timeframe_Divergence)) flags.push("TIMEFRAME_DIVERGENCE");
  if (activeRiskValue(row?.TEG_Squeeze_Risk)) flags.push("SQUEEZE_RISK");
  const sourceRisk = sourceRiskLevel(row?.TEG_Risk || row?.Source_Risk || row?.source_risk);
  return {
    source: "TEG", source_decision: model.playAction || model.sig || null, source_risk: sourceRisk,
    source_risk_flags: [...new Set(flags)],
    structured_risk: {
      exhaustion: row?.TEG_Exhaustion_Risk || null, divergence: row?.TEG_Divergence || null,
      timeframe_divergence: row?.TEG_Timeframe_Divergence || null, revision_context: row?.TEG_Revision_Context || null,
      squeeze_risk: row?.TEG_Squeeze_Risk || null,
    },
  };
}

const readStoredTegTheme = () => {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(TEG_THEME_KEY);
    return stored === "light" || stored === "dark" ? stored : "dark";
  } catch {
    return "dark";
  }
};

/**
 * TEGDashboard
 * Dedicated TEG / Earnings Guru dashboard view.
 * Clean card layout with compact trade-plan presentation and expandable technical detail.
 */
export default function TEGDashboard({
  rows = [],
  playerInput = "",
  setPlayerInput = () => {},
  selectedSector = "ALL",
  setSelectedSector = () => {},
  uniqueSectors = [],
  adminMode = false,
}) {
  const { hasGuruPlaysAccess } = useEntitlements();
  const [sortKey, setSortKey] = useState("day");
  const [sortDir, setSortDir] = useState("asc");
  const [view, setView] = useState("cards");
  const [expandedCards, setExpandedCards] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [calendarDate, setCalendarDate] = useState("ALL");
  const [structureFilter, setStructureFilter] = useState("ALL");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [setupStatusFilter, setSetupStatusFilter] = useState("ALL");
  const [setupDirectionFilter, setSetupDirectionFilter] = useState("ALL");
  const [expandedSetupCards, setExpandedSetupCards] = useState({});
  const [tegTheme, setTegTheme] = useState(readStoredTegTheme);
  const [guruPlays, setGuruPlays] = useState([]);
  const [playMakerTicker, setPlayMakerTicker] = useState(null);
  const [editingGuruPlay, setEditingGuruPlay] = useState(null);
  const isTegDark = tegTheme === "dark";

  // TEG scan filters — designed so you do not have to browse 50 cards manually.
  const [signalFilter, setSignalFilter] = useState("ALL"); // ALL | BULLISH | BEARISH | FLAT
  const [optionFilter, setOptionFilter] = useState("ALL"); // ALL | CALL | PUT | SPREAD | WAIT | NO_TRADE
  const [sessionFilter, setSessionFilter] = useState("ALL"); // ALL | PRE | POST
  const [qualityFilter, setQualityFilter] = useState("ALL"); // ALL | HIGH_PROB | TRADE_READY | WATCH | AVOID | SQUEEZE

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(max-width: 900px)");
    const sync = () => {
      setIsMobile(media.matches);
      if (media.matches) setView("cards");
    };
    sync();
    media.addEventListener?.("change", sync);
    return () => media.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen || typeof document === "undefined") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(TEG_THEME_KEY, tegTheme);
    } catch {
      if (tegTheme !== "dark") setTegTheme("dark");
    }
  }, [tegTheme]);

  const canViewGuruPlays = adminMode || hasGuruPlaysAccess("TEG");
  const refreshGuruPlays = () => canViewGuruPlays ? listGuruPlays({ source: "TEG", includeDrafts: adminMode }).then(setGuruPlays).catch(() => setGuruPlays([])) : Promise.resolve(setGuruPlays([]));
  useEffect(() => { refreshGuruPlays(); }, [adminMode, canViewGuruPlays]);

  const palette = {
    bg: isTegDark ? "#08111f" : "#f5f7fb",
    workspace: isTegDark ? "#0e1726" : "#ffffff",
    text: isTegDark ? "#f4f7fb" : "#101828",
    muted: isTegDark ? "#94a3b8" : "#667085",
    border: isTegDark ? "#2a394d" : "#d7dee8",
    card: isTegDark ? "#131e2e" : "#ffffff",
    soft: isTegDark ? "#182334" : "#f8fafc",
    pill: isTegDark ? "#182334" : "#f8fafc",
    accent: "#0ea5e9",
    call: isTegDark ? "#22c55e" : "#15803d",
    put: isTegDark ? "#ef4444" : "#dc2626",
    wait: isTegDark ? "#94a3b8" : "#64748b",
    watch: "#d97706",
    structured: "#7c3aed",
    elite: "#d4af37",
    navy: "#0f172a",
  };

  const themeToggle = (compact = false) => (
    <div className={`teg-theme-toggle ${compact ? "is-compact" : ""}`} role="group" aria-label="TEG color theme">
      {[
        ["light", "Light"],
        ["dark", "Dark"],
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          className={tegTheme === value ? "is-active" : ""}
          aria-pressed={tegTheme === value}
          aria-label={`Use ${label.toLowerCase()} TEG theme`}
          title={`Use ${label.toLowerCase()} TEG theme`}
          onClick={() => setTegTheme(value)}
        >
          <span aria-hidden="true">{value === "light" ? "☀" : "☾"}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const fmtPct = (v, digits = 1) => {
    const n = safeNum(v);
    return n === null ? "—" : `${n.toFixed(digits)}%`;
  };

  const fmtMoney = (v) => {
    const n = safeNum(v);
    return n === null ? "—" : `$${n.toFixed(2)}`;
  };

  const fmtNum = (v, digits = 1) => {
    const n = safeNum(v);
    return n === null ? "—" : n.toFixed(digits);
  };

  const compactLabel = (value) => {
    const s = String(value || "—").trim();
    return s
      .replace("REVERSAL_SQUEEZE_SETUP", "REVERSAL_SQZ")
      .replace("EXTREME_OVERBOUGHT", "EXT_OB")
      .replace("EXTREME_OVERSOLD", "EXT_OS")
      .replace("OVERBOUGHT", "OB")
      .replace("OVERSOLD", "OS")
      .replace("NORMAL_RISING", "RISING")
      .replace("NORMAL_FALLING", "FALLING")
      .replace("ELITE_REVISION_SUPPORT", "ELITE_REV")
      .replace("STRONG_REVISION_SUPPORT", "STRONG_REV")
      .replace("MODERATE_REVISION_SUPPORT", "MOD_REV")
      .replace("NEUTRAL_REVISION_SUPPORT", "NEUTRAL_REV")
      .replace("WEAK_REVISION_SUPPORT", "WEAK_REV")
      .replace("EARNINGS_MOMENTUM_EXCEPTION", "MOMENTUM_EXCEPTION")
      .replace("ATH_EXPANSION_CANDIDATE", "ATH_EXPANSION")
      .replace("ATH_EXPANSION_PROXY", "ATH_PROXY")
      .replace("HIGH_CONFIDENCE_REVERSAL", "HIGH_REV")
      .replace("ASYMMETRIC_REVERSAL", "ASYM_REV")
      .replace("SPECULATIVE_REVERSAL", "SPEC_REV")
      .replace("HIGH_CONFIDENCE_MOMENTUM", "HIGH_MOM")
      .replace("MEDIUM_CONFIDENCE", "MED_CONF")
      .replace("LOW_CONFIDENCE", "LOW_CONF")
      .replace("WATCH_ONLY", "WATCH")
      .replace("NO_EDGE_STATE", "NO_EDGE")
      .replace("REVERSAL_BASE_BUILDING", "REV_BASE")
      .replace("REVERSAL_SQUEEZE", "REV_SQZ")
      .replace("BREAKOUT_EXPANSION", "BREAKOUT_EXP")
      .replace("TREND_CONTINUATION", "TREND_CONT")
      .replace("DOWNSIDE_CONTINUATION", "DOWNSIDE_CONT")
      .replace("EXHAUSTION_RISK", "EXHAUSTION")
      .replace("CHASE_RISK", "CHASE_RISK")
      .replace("BOUNCE_RISK", "BOUNCE_RISK")
      .replace("MIXED_STATE", "MIXED")
      .replace("LATE_CYCLE_NO_EDGE", "LATE_NO_EDGE")
      .replace("NO_CLEAN_CONDITION", "NO_CLEAN")
      .replace("REVERSAL_WITH_RUNWAY", "REV_RUNWAY")
      .replace("EARLY_REVERSAL", "EARLY_REV")
      .replace("DIVERGENCE_EXHAUSTION_RISK", "DIV_EXHAUST")
      .replace("BULLISH_DIVERGENCE_BUILDING", "BULL_DIV")
      .replace("MIXED_WITH_BOUNCE_RISK", "MIX_BOUNCE")
      .replace("MIXED_WITH_ROLLOVER_RISK", "MIX_ROLLOVER")
      .replace("TIMEFRAME_CONFLICT", "TF_CONFLICT")
      .replace("MOMENTUM_EXCEPTION", "MOM_EXCEPTION")
      .replace("HIGH_RANGE_CONTINUATION", "HIGH_CONT")
      .replace("HIGH_RANGE_CHASE_RISK", "CHASE_RISK")
      .replace("LOW_RANGE_BOUNCE_RISK", "LOW_BOUNCE")
      .replace("CLEAN_BULLISH_CONTINUATION", "CLEAN_BULL")
      .replace("CLEAN_BEARISH_CONTINUATION", "CLEAN_BEAR")
      .replace("MIXED_CONDITION", "MIXED_COND")
      .replace("BULLISH_TREND_WITH_STOCH_ROLLOVER", "BULL_TREND_ROLLOVER")
      .replace("BEARISH_TREND_WITH_STOCH_CURL", "BEAR_TREND_CURL")
      .replace("THREED_WEEKLY_MACD_CONFLICT", "3D_W_MACD_CONFLICT")
      .replace("THREED_UP_WEEKLY_DOWN", "3D_UP_W_DOWN")
      .replace("THREED_DOWN_WEEKLY_UP", "3D_DOWN_W_UP")
      .replace("52W_NEAR_HIGH", "52W_HIGH")
      .replace("52W_NEAR_LOW", "52W_LOW")
      .replace("52W_LOW_BASE", "52W_BASE")
      .replace("52W_UPPER_RANGE", "52W_UPPER")
      .replace("52W_LOWER_RANGE", "52W_LOWER")
      .replace("52W_MID_RANGE", "52W_MID")
      .replace("NEAR_52W_HIGH", "52W_HIGH")
      .replace("NEAR_52W_LOW", "52W_LOW")
      .replace("LOW_BASE_BUILDING", "52W_BASE")
      .replace("UPPER_52W_RANGE", "52W_UPPER")
      .replace("LOWER_52W_RANGE", "52W_LOWER")
      .replace("MID_52W_RANGE", "52W_MID");
  };

  const tvUrl = (ticker) => {
    if (!ticker) return null;
    return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(ticker)}`;
  };

  const getText = (r, keys = []) => {
    for (const k of keys) {
      const v = r?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };

  const getOptionValue = (r) =>
    getText(r, ["TEG_Option_Strategy", "Option_Strategy", "option_strategy", "strategy", "Options"])
      .toUpperCase();

  const getEntryValue = (r) =>
    getText(r, ["TEG_Entry_Timing", "Entry_Timing", "entry_timing", "entry"])
      .toUpperCase();

  const getSignalValue = (r) =>
    getText(r, ["TEG_Signal", "signal"])
      .toUpperCase();

  const getReportValue = (r) =>
    getText(r, ["report_time", "ReportTime"])
      .toLowerCase();

  const getSetupValue = (r) =>
    getText(r, ["TEG_Setup", "setup", "TEG_Regime", "TEG_Regime_Label", "TEG_Condition"])
      .toUpperCase();

  const getConfidenceValue = (r) =>
    getText(r, ["TEG_Confidence_Layer", "confidence_layer", "Confidence", "confidence"])
      .toUpperCase();

  const getActionValue = (r) =>
    getText(r, ["TEG_Action", "TEG_Tradability", "TEG_Structure_Action", "action"])
      .toUpperCase();

  const isTradeReady = (r) => {
    const entry = getEntryValue(r);
    const opt = getOptionValue(r);
    const action = getActionValue(r);
    const conf = getConfidenceValue(r);
    return (
      !entry.includes("NO_TRADE") &&
      !entry.includes("WAIT") &&
      !opt.includes("NO_TRADE") &&
      !opt.includes("WAIT") &&
      !action.includes("AVOID") &&
      !action.includes("NO_TRADE") &&
      (action.includes("TRADE") || entry.includes("PRE_OK") || entry.includes("EARLY") || conf.includes("HIGH") || conf.includes("MED"))
    );
  };

  const isHighProbability = (r) => {
    const confScore = safeNum(r?.TEG_Confidence_Score ?? r?.confidence_score ?? r?.ConfScore);
    const score = safeNum(r?.TEG_Score_Total ?? r?.TEG_Score ?? r?.score);
    const conf = getConfidenceValue(r);
    const entry = getEntryValue(r);
    const opt = getOptionValue(r);
    const isActionable = !entry.includes("NO_TRADE") && !opt.includes("NO_TRADE") && !opt.includes("WAIT");

    return (
      isActionable &&
      (
        conf.includes("HIGH") ||
        conf.includes("MED") ||
        (confScore !== null && confScore >= 3) ||
        (score !== null && Math.abs(score) >= 4)
      )
    );
  };

  const matchesQuality = (r) => {
    const entry = getEntryValue(r);
    const opt = getOptionValue(r);
    const setup = getSetupValue(r);
    const conf = getConfidenceValue(r);
    const action = getActionValue(r);
    const reason = getText(r, ["TEG_Reason", "TEG_Condition", "TEG_Condition_Label", "TEG_Regime", "TEG_Regime_Label", "TEG_Market_State"])
      .toUpperCase();

    switch (qualityFilter) {
      case "HIGH_PROB":
        return isHighProbability(r);
      case "TRADE_READY":
        return isTradeReady(r);
      case "WATCH":
        return entry.includes("WAIT") || action.includes("WATCH") || conf.includes("WATCH") || reason.includes("WATCH");
      case "AVOID":
        return entry.includes("NO_TRADE") || opt.includes("NO_TRADE") || action.includes("AVOID") || reason.includes("NO_EDGE");
      case "SQUEEZE":
        return setup.includes("SQUEEZE") || reason.includes("SQUEEZE") || setup.includes("SQZ");
      default:
        return true;
    }
  };

  const resetScanFilters = () => {
    setSignalFilter("ALL");
    setOptionFilter("ALL");
    setSessionFilter("ALL");
    setQualityFilter("ALL");
    setCalendarDate("ALL");
    setStructureFilter("ALL");
    setMinPrice("");
    setMaxPrice("");
    setSetupStatusFilter("ALL");
    setSetupDirectionFilter("ALL");
    setSortKey("day");
    setSortDir("asc");
  };

  const applyPreset = (preset) => {
    if (preset === "CALLS") {
      setOptionFilter("CALL"); setSignalFilter("BULLISH"); setQualityFilter("ALL"); setSortKey("TEG_Score_Total"); setSortDir("desc");
    } else if (preset === "PUTS") {
      setOptionFilter("PUT"); setSignalFilter("BEARISH"); setQualityFilter("ALL"); setSortKey("TEG_Score_Total"); setSortDir("asc");
    } else if (preset === "PRE") {
      setSessionFilter("PRE"); setQualityFilter("ALL"); setSortKey("TEG_Confidence_Score"); setSortDir("desc");
    } else if (preset === "POST") {
      setSessionFilter("POST"); setQualityFilter("ALL"); setSortKey("TEG_Confidence_Score"); setSortDir("desc");
    } else if (preset === "HIGH_PROB") {
      setQualityFilter("HIGH_PROB"); setSortKey("TEG_Confidence_Score"); setSortDir("desc");
    } else if (preset === "TRADE_READY") {
      setQualityFilter("TRADE_READY"); setSortKey("TEG_Confidence_Score"); setSortDir("desc");
    } else if (preset === "WATCH") {
      setQualityFilter("WATCH"); setSortKey("TEG_Confidence_Score"); setSortDir("desc");
    } else if (preset === "SQUEEZE") {
      setQualityFilter("SQUEEZE"); setSortKey("TEG_Score_Total"); setSortDir("desc");
    }
  };
  
  const rowDate = (r) => String(r?.earnings_date || r?.Earnings_Date || r?.date || "").slice(0, 10);

  const structureState = (r) => {
    const d3 = safeNum(r?.["3d_delta_from_ma50_pct"] ?? r?.ma3d_delta_from_ma50);
    const w1 = safeNum(r?.["1w_delta_from_ma50_pct"] ?? r?.ma1w_delta_from_ma50);
    const reason = getText(r, ["TEG_Reason", "TEG_Structure_Gate_Reason", "TEG_Condition_Reason"]).toUpperCase();
    return {
      d3,
      w1,
      above3d: d3 !== null && d3 >= 0,
      below3d: d3 !== null && d3 < 0,
      above1w: w1 !== null && w1 >= 0,
      below1w: w1 !== null && w1 < 0,
      reclaimCandidate: reason.includes("RECLAIM") && d3 !== null && d3 < 0,
      alignedAbove: d3 !== null && w1 !== null && d3 >= 0 && w1 >= 0,
      alignedBelow: d3 !== null && w1 !== null && d3 < 0 && w1 < 0,
    };
  };

  const matchesStructure = (r) => {
    const st = structureState(r);
    switch (structureFilter) {
      case "3D_ABOVE_50": return st.above3d;
      case "3D_BELOW_50": return st.below3d;
      case "3D_RECLAIM_TARGET": return st.reclaimCandidate;
      case "WEEKLY_ABOVE_50": return st.above1w;
      case "WEEKLY_BELOW_50": return st.below1w;
      case "ALIGNED_ABOVE": return st.alignedAbove;
      case "ALIGNED_BELOW": return st.alignedBelow;
      default: return true;
    }
  };

  const calendarGroups = useMemo(() => {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const date = rowDate(r) || "Unknown";
      if (!map.has(date)) map.set(date, { date, total: 0, pre: 0, post: 0, calls: 0, puts: 0 });
      const g = map.get(date);
      g.total += 1;
      const report = getReportValue(r);
      if (report.includes("pre")) g.pre += 1;
      if (report.includes("post")) g.post += 1;
      const opt = getOptionValue(r);
      if (opt.includes("CALL")) g.calls += 1;
      if (opt.includes("PUT")) g.puts += 1;
    });
    return [...map.values()].sort((a, b) => {
      if (a.date === "Unknown") return 1;
      if (b.date === "Unknown") return -1;
      return a.date.localeCompare(b.date);
    });
  }, [rows]);

  const formatCalendarDate = (value) => {
    if (!value || value === "Unknown") return "Date unavailable";
    const d = new Date(`${value}T12:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };

  const filteredRows = useMemo(() => {
    const q = String(playerInput || "").trim().toLowerCase();
    const sectorSel = String(selectedSector || "ALL").trim().toLowerCase();
    const minPriceNum = String(minPrice).trim() === "" ? null : safeNum(minPrice);
    const maxPriceNum = String(maxPrice).trim() === "" ? null : safeNum(maxPrice);
    const priceFilterActive = minPriceNum !== null || maxPriceNum !== null;

    return (Array.isArray(rows) ? rows : []).filter((r) => {
      const text = [
        r?.ticker,
        r?.Ticker,
        r?.symbol,
        r?.company_earnings,
        r?.company,
        r?.Company,
        r?.Name,
      ].map((v) => String(v ?? "").toLowerCase()).join(" ");

      const sector = String(r?.Sector ?? r?.sector ?? r?.Industry ?? r?.Category ?? r?.SubSector ?? "").toLowerCase();
      const sig = getSignalValue(r);
      const opt = getOptionValue(r);
      const report = getReportValue(r);

      const matchesSearch = !q || text.includes(q);
      const matchesSector = !sectorSel || sectorSel === "all" || sector === sectorSel || sector.includes(sectorSel);
      const price = safeNum(r?.price);
      const matchesPrice = !priceFilterActive || (
        price !== null &&
        (minPriceNum === null || price >= minPriceNum) &&
        (maxPriceNum === null || price <= maxPriceNum)
      );
      const matchesSignal = signalFilter === "ALL" || sig.includes(signalFilter);
      const matchesOption =
        optionFilter === "ALL" ||
        (optionFilter === "SPREAD" && opt.includes("SPREAD")) ||
        (optionFilter === "WAIT" && (opt.includes("WAIT") || getEntryValue(r).includes("WAIT"))) ||
        (optionFilter === "NO_TRADE" && (opt.includes("NO_TRADE") || getEntryValue(r).includes("NO_TRADE"))) ||
        opt.includes(optionFilter);
      const matchesSession =
        sessionFilter === "ALL" ||
        (sessionFilter === "PRE" && report.includes("pre")) ||
        (sessionFilter === "POST" && report.includes("post"));
      const matchesCalendar = calendarDate === "ALL" || rowDate(r) === calendarDate;
      const privateSetup = getPrivateTegSetup(r, adminMode);
      const matchesSetupStatus = !adminMode || setupStatusFilter === "ALL" || privateSetup?.status === setupStatusFilter;
      const matchesSetupDirection = !adminMode || setupDirectionFilter === "ALL" || privateSetup?.direction === setupDirectionFilter;

      return matchesSearch && matchesSector && matchesPrice && matchesSignal && matchesOption && matchesSession && matchesCalendar && matchesStructure(r) && matchesQuality(r) && matchesSetupStatus && matchesSetupDirection;
    });
  }, [rows, playerInput, selectedSector, minPrice, maxPrice, signalFilter, optionFilter, sessionFilter, qualityFilter, calendarDate, structureFilter, setupStatusFilter, setupDirectionFilter, adminMode]);

  const sorted = useMemo(() => {
    const arr = Array.isArray(filteredRows) ? [...filteredRows] : [];
    const dir = sortDir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      const av = a?.[sortKey] ?? "";
      const bv = b?.[sortKey] ?? "";
      const an = Number(av);
      const bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
      if (sortKey === "as_of") {
        const ad = new Date(av).getTime();
        const bd = new Date(bv).getTime();
        if (Number.isFinite(ad) && Number.isFinite(bd)) return (ad - bd) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });

    return arr;
  }, [filteredRows, sortKey, sortDir]);

  const scanCounts = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return {
      total: base.length,
      calls: base.filter((r) => getOptionValue(r).includes("CALL")).length,
      puts: base.filter((r) => getOptionValue(r).includes("PUT")).length,
      pre: base.filter((r) => getReportValue(r).includes("pre")).length,
      post: base.filter((r) => getReportValue(r).includes("post")).length,
      highProb: base.filter(isHighProbability).length,
      tradeReady: base.filter(isTradeReady).length,
      squeeze: base.filter((r) => getSetupValue(r).includes("SQUEEZE") || getSetupValue(r).includes("SQZ")).length,
    };
  }, [rows]);

  // Setup counts intentionally follow the selected earnings date.  They do not
  // collapse when a setup-status filter is selected, so the owner can move
  // between buckets without losing the universe summary.
  const setupCounts = useMemo(() => {
    if (!adminMode) return null;
    const base = (Array.isArray(rows) ? rows : []).filter((r) => calendarDate === "ALL" || rowDate(r) === calendarDate);
    const counts = { STRONG_MATCH: 0, MATCH: 0, NEAR_MATCH: 0, NO_MATCH: 0, PREMIUM_PROTECTION: 0 };
    base.forEach((r) => {
      const status = getPrivateTegSetup(r, true)?.status;
      if (Object.prototype.hasOwnProperty.call(counts, status)) counts[status] += 1;
    });
    return counts;
  }, [rows, calendarDate, adminMode]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const signalTone = (sig) => {
    const s = String(sig || "").toUpperCase();
    if (s.includes("BULL")) return "bull";
    if (s.includes("BEAR")) return "bear";
    if (s.includes("FLAT") || s.includes("NEUTRAL")) return "warn";
    return "neutral";
  };

  const getDirection = (r, model = null) => {
    const opt = String(model?.opt || getOptionValue(r) || "").toUpperCase();
    const sig = String(model?.sig || getSignalValue(r) || "").toUpperCase();

    const isCall = opt.includes("CALL");
    const isPut = opt.includes("PUT");
    const isBull = sig.includes("BULL");
    const isBear = sig.includes("BEAR");
    const isStructured = opt.includes("SPREAD") || opt.includes("STRADDLE") || opt.includes("STRANGLE");

    // Keep direction visually honest:
    // green = bullish CALL alignment, red = bearish PUT alignment,
    // purple = reversal/conflict/structured trade, gray = no directional trade.
    if (isStructured || (isCall && isBear) || (isPut && isBull) || (isCall && isPut)) return "PUT / CALL";
    if (isCall && (isBull || !sig)) return "CALL";
    if (isPut && (isBear || !sig)) return "PUT";
    if (!opt && isBull) return "CALL";
    if (!opt && isBear) return "PUT";
    return "WAIT";
  };

  const directionStyle = (direction) => {
    if (direction === "CALL") return { color: palette.call, soft: isTegDark ? "rgba(22,163,74,0.17)" : "rgba(22,163,74,0.10)" };
    if (direction === "PUT") return { color: palette.put, soft: isTegDark ? "rgba(220,38,38,0.17)" : "rgba(220,38,38,0.09)" };
    if (direction === "PUT / CALL") return { color: palette.structured, soft: isTegDark ? "rgba(124,58,237,0.18)" : "rgba(124,58,237,0.10)" };
    return { color: palette.wait, soft: isTegDark ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.12)" };
  };

  const executionMeta = (action, timing) => {
    const a = `${action || ""} ${timing || ""}`.toUpperCase();
    if (a.includes("AVOID") || a.includes("NO_TRADE") || a.includes("NO TIMING")) return { label: "AVOID", detail: "No clean edge", color: palette.put };
    if (a.includes("TRADE_READY") || a.includes("EARLY_ENTRY") || a.includes("IMPROVING")) return { label: "EXECUTION READY", detail: a.includes("EARLY") ? "Early entry" : "Setup aligned", color: palette.call };
    if (a.includes("STRUCTURED")) return { label: "STRUCTURED", detail: "Defined-risk options", color: palette.structured };
    if (a.includes("WATCH")) return { label: "WATCH", detail: "Setup developing", color: palette.watch };
    if (a.includes("PULLBACK")) return { label: "WAIT", detail: "For pullback", color: palette.wait };
    return { label: "WAIT", detail: "For confirmation", color: palette.wait };
  };

  const stochTone = (state) => {
    const s = String(state || "").toUpperCase();
    if (s.includes("OVERBOUGHT") || s.includes("WAIT") || s.includes("PULLBACK")) return "warn";
    if (s.includes("OVERSOLD") || s.includes("BOUNCE")) return "warn";
    if (s.includes("BULL") || s.includes("UP") || s.includes("RISING") || s === "K>D") return "bull";
    if (s.includes("BEAR") || s.includes("DOWN") || s.includes("FALLING") || s === "K<D") return "bear";
    return "neutral";
  };


  const humanRisk = (value) => {
    const s = String(value || "").trim();
    const u = s.toUpperCase();
    const map = {
      "3D_W_MACD_CONFLICT": "3D and weekly momentum conflict",
      "THREED_WEEKLY_MACD_CONFLICT": "3D and weekly momentum conflict",
      "TF_CONFLICT": "Timeframe conflict",
      "BEAR_TREND_CURL": "Downtrend is curling — reversal risk",
      "BULL_TREND_ROLLOVER": "Bull trend is rolling over",
      "MACD LAGGING BEARISH": "MACD still lagging bearish",
      "BELOW 3D 50MA": "Below 3D 50MA",
      "50MA ABOVE PRICE": "3D 50MA still above price",
      "WEAK REVISIONS": "Weak revisions",
      "NEAR 52W HIGH / CHASE RISK": "Near 52W high — chase risk",
      "NEAR 52W LOW / BOUNCE RISK": "Near 52W low — bounce risk",
    };
    if (map[u]) return map[u];
    return compactLabel(s).replaceAll("_", " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());
  };

  const entryTone = (entry) => {
    const e = String(entry || "").toUpperCase();
    if (e.includes("NO_TRADE") || e.includes("WAIT")) return "warn";
    if (e.includes("PRE_OK") || e.includes("EARLY")) return "bull";
    if (e.includes("SPREAD")) return "bull";
    return "neutral";
  };

  const actionTone = (action) => {
    const a = String(action || "").toUpperCase();
    if (a.includes("AVOID") || a.includes("NO TRADE")) return "warn";
    if (a.includes("WATCH") || a.includes("WAIT") || a.includes("STRUCTURED")) return "warn";
    if (a.includes("ENTER") || a.includes("EARLY") || a.includes("TRADE")) return "bull";
    return "neutral";
  };

  const chip = (text, tone = "neutral", opts = {}) => {
    const raw = text || "—";
    const label = opts.compact === false ? raw : compactLabel(raw);

    const bg =
      tone === "bull"
        ? isTegDark ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.12)"
        : tone === "bear"
          ? isTegDark ? "rgba(239,68,68,0.18)" : "rgba(239,68,68,0.10)"
          : tone === "warn"
            ? isTegDark ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.12)"
            : palette.pill;

    const br =
      tone === "bull"
        ? isTegDark ? "rgba(16,185,129,0.35)" : "rgba(16,185,129,0.28)"
        : tone === "bear"
          ? isTegDark ? "rgba(239,68,68,0.35)" : "rgba(239,68,68,0.25)"
          : tone === "warn"
            ? isTegDark ? "rgba(245,158,11,0.35)" : "rgba(245,158,11,0.28)"
            : palette.border;

    return (
      <span
        title={String(raw)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: opts.maxWidth || "100%",
          minWidth: 0,
          padding: "0.22rem 0.55rem",
          borderRadius: 999,
          border: `1px solid ${br}`,
          background: bg,
          fontWeight: 850,
          letterSpacing: 0.15,
          fontSize: opts.fontSize || 12,
          color: palette.text,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: 1.2,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </span>
    );
  };

  const stochRsiStateFallback = (k, d) => {
    const kk = safeNum(k);
    const dd = safeNum(d);
    if (kk === null && dd === null) return "—";
    const ref = kk ?? dd;
    if (ref >= 80) return "OVERBOUGHT";
    if (ref <= 20) return "OVERSOLD";
    if (kk !== null && dd !== null && kk > dd) return "K>D";
    if (kk !== null && dd !== null && kk < dd) return "K<D";
    return "NORMAL";
  };

  const getCardModel = (r) => {
    const ticker = r?.ticker || r?.Ticker || r?.symbol;
    const setup = r?.TEG_Setup || r?.setup || "";
    const sig = r?.TEG_Signal || r?.signal || "";
    const entry = r?.TEG_Entry_Timing || r?.Entry_Timing || r?.entry_timing || r?.entry || "";
    const opt = r?.TEG_Option_Strategy || r?.Option_Strategy || r?.option_strategy || r?.strategy || "";
    const reason = r?.TEG_Reason || r?.reason || "";

    const bbUp = r?.["3d_bb_room_up_pct"] ?? r?.BB_Room_Up_Pct;
    const bbDn = r?.["3d_bb_room_down_pct"] ?? r?.BB_Room_Down_Pct;
    const wBbUp = r?.["1w_bb_room_up_pct"] ?? r?.W_BB_Room_Up_Pct;
    const wBbDn = r?.["1w_bb_room_down_pct"] ?? r?.W_BB_Room_Down_Pct;

    const daily20 = r?.daily_ma20;
    const daily50 = r?.daily_ma50;
    const daily200 = r?.daily_ma200;
    const ma3d20 = r?.ma3d_20;
    const ma3d50 = r?.ma3d_50;
    const ma3d200 = r?.ma3d_200;
    const ma1w20 = r?.ma1w_20;
    const ma1w50 = r?.ma1w_50;
    const ma1w200 = r?.ma1w_200;

    const daily50Delta = r?.daily_delta_from_ma50_pct ?? r?.daily_delta_from_ma50;
    const ma3d50Delta = r?.["3d_delta_from_ma50_pct"] ?? r?.ma3d_delta_from_ma50;
    const ma1w50Delta = r?.["1w_delta_from_ma50_pct"] ?? r?.ma1w_delta_from_ma50;

    const stoch1dK = r?.tf1d_stoch_rsi_k ?? r?.tf_1d_stoch_rsi_k;
    const stoch1dD = r?.tf1d_stoch_rsi_d ?? r?.tf_1d_stoch_rsi_d;
    const stoch3dK = r?.tf3d_stoch_rsi_k ?? r?.tf_3d_stoch_rsi_k;
    const stoch3dD = r?.tf3d_stoch_rsi_d ?? r?.tf_3d_stoch_rsi_d;
    const stoch1wK = r?.tf1w_stoch_rsi_k ?? r?.tf_1w_stoch_rsi_k;
    const stoch1wD = r?.tf1w_stoch_rsi_d ?? r?.tf_1w_stoch_rsi_d;

    const stoch1dState = r?.TEG_StochRSI_1D_State ?? stochRsiStateFallback(stoch1dK, stoch1dD);
    const stoch3dState = r?.TEG_StochRSI_3D_State ?? stochRsiStateFallback(stoch3dK, stoch3dD);
    const stoch1wState = r?.TEG_StochRSI_1W_State ?? stochRsiStateFallback(stoch1wK, stoch1wD);

    const divergence = r?.TEG_Divergence || "NONE";
    const exhaustionRisk = r?.TEG_Exhaustion_Risk || "NONE";
    const momentumException = r?.TEG_Momentum_Exception || "NONE";
    const revisionContext = r?.TEG_Revision_Context || "";
    const near52wHigh = r?.TEG_Near_52W_High === true || String(r?.TEG_Near_52W_High).toLowerCase() === "true";
    const athExpansionProxy = r?.TEG_ATH_Expansion_Proxy === true || String(r?.TEG_ATH_Expansion_Proxy).toLowerCase() === "true";

    // 52-week structure layer from final TEG builder
    const high52w = r?.TEG_52W_High ?? r?.high_52w;
    const low52w = r?.TEG_52W_Low ?? r?.low_52w;
    const pctBelow52wHigh = r?.TEG_Pct_Below_52W_High;
    const pctAbove52wLow = r?.TEG_Pct_Above_52W_Low;
    const pos52w = r?.TEG_52W_Position_Pct;
    const context52w = r?.TEG_52W_Context || (near52wHigh ? "NEAR_52W_HIGH" : "");
    const reason52w = r?.TEG_52W_Reason || "";
    const display52wLabel = r?.TEG_52W_Display_Label || "";
    const display52wReason = r?.TEG_52W_Display_Reason || reason52w || "";
    const marketState = r?.TEG_Market_State || "";
    const marketStateLabel = r?.TEG_Market_State_Label || compactLabel(marketState) || "";
    const marketStateReason = r?.TEG_Market_State_Reason || "";
    const condition = r?.TEG_Condition || "";
    const conditionLabel = r?.TEG_Condition_Label || compactLabel(condition) || "";
    const conditionReason = r?.TEG_Condition_Reason || "";
    const timeframeDivergence = r?.TEG_Timeframe_Divergence || "NONE";
    const timeframeDivergenceReason = r?.TEG_Timeframe_Divergence_Reason || "";

    const hasWeakRevision = String(revisionContext || "").toUpperCase().includes("WEAK");
    const hasStrongRevision = ["STRONG", "ELITE"].some((x) => String(revisionContext || "").toUpperCase().includes(x));
    const macdLaggingBearish = String(reason || "").includes("MACD hist -");
    const below3d50 = safeNum(ma3d50Delta) !== null && safeNum(ma3d50Delta) < -2;

    const setupSummary = (() => {
      if (String(setup).includes("REVERSAL_SQUEEZE")) return "3D reversal base + Stoch RSI curl";
      if (String(setup).includes("COILED")) return "Coiled near 3D 50MA";
      if (String(setup).includes("ATH") || athExpansionProxy || String(context52w).includes("NEAR_52W_HIGH")) return "Momentum expansion near 52W high";
      if (String(setup).includes("OVERBOUGHT")) return "Trend strong but extended";
      if (String(setup).includes("TREND_DOWN")) return "Bearish trend continuation";
      if (String(setup).includes("TREND_UP")) return "Bullish trend continuation";
      return setup || sig || "Mixed setup";
    })();

    const targetSummary = (() => {
      const squeezeTarget = r?.TEG_3D_Squeeze_Target || r?.teg_3d_squeeze_target;
      const squeezePct = r?.TEG_3D_Squeeze_Target_Pct || r?.teg_3d_squeeze_target_pct;
      if (squeezeTarget) return `${compactLabel(squeezeTarget)}${safeNum(squeezePct) !== null ? ` (${fmtPct(squeezePct)})` : ""}`;
      if (String(setup).includes("REVERSAL_SQUEEZE")) {
        if (safeNum(ma3d50) !== null) return `3D 50MA reclaim near ${fmtMoney(ma3d50)}; upper BB runway ${fmtPct(bbUp)}`;
        return `Upper 3D BB runway ${fmtPct(bbUp)}`;
      }
      if (sig === "BULLISH") return `Upside room: 3D ${fmtPct(bbUp)} / W ${fmtPct(wBbUp)}`;
      if (sig === "BEARISH") return `Downside room: 3D ${fmtPct(bbDn)} / W ${fmtPct(wBbDn)}`;
      return "No clean target";
    })();

    const riskSummary = (() => {
      const risks = [];
      if (hasWeakRevision) risks.push("weak revisions");
      if (macdLaggingBearish && sig === "BULLISH") risks.push("MACD lagging bearish");
      if (below3d50 && !String(setup).includes("REVERSAL_SQUEEZE")) risks.push("below 3D 50MA");
      if (below3d50 && String(setup).includes("REVERSAL_SQUEEZE")) risks.push("50MA above price");
      if (exhaustionRisk !== "NONE") risks.push(compactLabel(exhaustionRisk));
      if (divergence !== "NONE") risks.push(compactLabel(divergence));
      if (timeframeDivergence !== "NONE") risks.push(compactLabel(timeframeDivergence));
      if (sig === "BULLISH" && String(context52w).includes("NEAR_52W_HIGH") && !athExpansionProxy) risks.push("near 52W high / chase risk");
      if (sig === "BEARISH" && String(context52w).includes("NEAR_52W_LOW")) risks.push("near 52W low / bounce risk");
      if (!risks.length && hasStrongRevision) risks.push("revision support strong");
      return risks.length ? risks.map(humanRisk).join("; ") : "No major risk flag";
    })();


    const playAction = (() => {
      const e = String(entry || "").toUpperCase();
      const c = String(r?.TEG_Confidence_Layer || "").toUpperCase();
      if (e.includes("NO_TRADE")) return "AVOID";
      if (e.includes("WAIT")) return "WAIT";
      if (e.includes("SPREAD_ONLY")) return "STRUCTURED";
      if (e.includes("PRE_OK_EARLY")) return "EARLY_ENTRY";
      if (e.includes("PRE_OK") && (c.includes("HIGH") || c.includes("ASYMMETRIC") || c.includes("MEDIUM"))) return "TRADE_READY";
      if (e.includes("PRE_OK")) return "WATCH_ENTRY";
      return e || "WATCH";
    })();

    const playType = (() => {
      const s = `${setup} ${condition} ${marketState}`.toUpperCase();
      if (s.includes("REVERSAL") || s.includes("SQUEEZE") || s.includes("BOUNCE")) return "REVERSAL";
      if (s.includes("ATH") || s.includes("52W") || s.includes("BREAKOUT") || s.includes("EXPANSION")) return "EXPANSION";
      if (s.includes("TREND") || sig === "BULLISH" || sig === "BEARISH") return "CONTINUATION";
      if (s.includes("CONFLICT") || s.includes("MIXED")) return "MIXED";
      return "SCAN";
    })();

    const timingQuality = (() => {
      const e = String(entry || "").toUpperCase();
      const st3 = String(stoch3dState || "").toUpperCase();
      if (e.includes("NO_TRADE")) return "NO_TIMING";
      if (e.includes("WAIT")) return "WAIT_FOR_TRIGGER";
      if (e.includes("EARLY")) return "EARLY";
      if (st3.includes("FALLING") && sig === "BULLISH") return "PULLBACK_TIMING";
      if (st3.includes("RISING") && sig === "BULLISH") return "IMPROVING";
      if (st3.includes("RISING") && sig === "BEARISH") return "BOUNCE_RISK";
      return "ACCEPTABLE";
    })();

    const eliteHeadline = (() => {
      if (playAction === "AVOID") return "No trade — edge is not clean enough";
      if (playAction === "WAIT") return "Wait for confirmation or a cleaner trigger";
      if (playAction === "STRUCTURED") return "Structured trade only — manage IV and extension risk";
      if (playAction === "EARLY_ENTRY") return "Early reversal entry — asymmetric, but still developing";
      if (playAction === "TRADE_READY") return "Trade-ready setup with supportive context";
      return "Watchlist candidate — validate timing before entry";
    })();

    const eliteSubtext = (() => {
      const parts = [];
      if (conditionLabel) parts.push(conditionLabel);
      if (display52wLabel) parts.push(compactLabel(display52wLabel));
      return parts.join(" • ");
    })();

    return {
      ticker,
      playAction,
      playType,
      timingQuality,
      eliteHeadline,
      eliteSubtext,
      setup,
      sig,
      entry,
      opt,
      reason,
      bbUp,
      bbDn,
      wBbUp,
      wBbDn,
      daily20,
      daily50,
      daily200,
      ma3d20,
      ma3d50,
      ma3d200,
      ma1w20,
      ma1w50,
      ma1w200,
      daily50Delta,
      ma3d50Delta,
      ma1w50Delta,
      stoch1dK,
      stoch1dD,
      stoch3dK,
      stoch3dD,
      stoch1wK,
      stoch1wD,
      stoch1dState,
      stoch3dState,
      stoch1wState,
      divergence,
      divergenceReason: r?.TEG_Divergence_Reason || "",
      exhaustionRisk,
      exhaustionReason: r?.TEG_Exhaustion_Reason || "",
      momentumException,
      momentumExceptionReason: r?.TEG_Momentum_Exception_Reason || "",
      revisionContext,
      revisionReason: r?.TEG_Revision_Reason || "",
      near52wHigh,
      athExpansionProxy,
      high52w,
      low52w,
      pctBelow52wHigh,
      pctAbove52wLow,
      pos52w,
      context52w,
      reason52w,
      display52wLabel,
      display52wReason,
      marketState,
      marketStateLabel,
      marketStateReason,
      condition,
      conditionLabel,
      conditionReason,
      timeframeDivergence,
      timeframeDivergenceReason,
      confidenceLayer: r?.TEG_Confidence_Layer || r?.TEG_Conviction || "",
      confidenceScore: r?.TEG_Confidence_Score,
      confidenceReason: r?.TEG_Confidence_Reason || "",
      setupSummary,
      targetSummary,
      riskSummary,
    };
  };

  const sectionBox = (children, extra = {}) => (
    <div
      style={{
        marginTop: 10,
        padding: "10px 12px",
        borderRadius: 14,
        background: palette.soft,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        fontSize: 12,
        lineHeight: 1.45,
        minWidth: 0,
        overflow: "hidden",
        ...extra,
      }}
    >
      {children}
    </div>
  );

  const card = (r, idx) => {
    const m = getCardModel(r);
    const ticker = m.ticker;
    const company = r?.company_earnings || r?.company || "";
    const reportTime = r?.report_time || r?.ReportTime || "";
    const day = r?.day || r?.Day || "";
    const price = r?.price;
    const pct1m = r?.pct_1m;
    const pct3m = r?.pct_3m;
    const pctytd = r?.pct_ytd;
    const link = tvUrl(ticker);
    const cardKey = `${ticker}-${idx}`;
    const isExpanded = !!expandedCards[cardKey];
    const isSetupExpanded = !!expandedSetupCards[cardKey];
    const privateSetup = getPrivateTegSetup(r, adminMode);
    const direction = getDirection(r, m);
    const directionMeta = directionStyle(direction);
    const execution = executionMeta(m.playAction, m.timingQuality);
    const tickerGuruPlay = canViewGuruPlays ? selectCurrentGuruPlay(guruPlays.filter((play) => play.ticker === ticker && (adminMode || play.publication_state === "PUBLISHED"))) : null;
    const canOpenPlayMaker = adminMode && !["WAIT", "AVOID"].includes(m.playAction);
    const isElite = String(m.confidenceLayer || "").toUpperCase().includes("ELITE") ||
      String(m.confidenceLayer || "").toUpperCase().includes("HIGH") ||
      (safeNum(m.confidenceScore) !== null && safeNum(m.confidenceScore) >= 4);

    return (
      <div
        className={`teg-result-card ${(isExpanded || isSetupExpanded) ? "is-expanded" : "is-collapsed"}`}
        key={cardKey}
        style={{
          position: "relative",
          border: `1px solid ${palette.border}`,
          borderLeft: `7px solid ${directionMeta.color}`,
          background: palette.card,
          borderRadius: 18,
          padding: 16,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", inset: "0 0 auto 0", height: 4, background: `linear-gradient(90deg, ${directionMeta.color}, transparent 72%)`, opacity: 0.9 }} />

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div className="teg-editorial" style={{ fontSize: 24, fontWeight: 950, letterSpacing: 0.2 }}>{ticker || "—"}</div>
              <span style={{ padding: "5px 10px", borderRadius: 999, background: directionMeta.color, color: "#fff", fontWeight: 950, fontSize: 12, letterSpacing: 0.45 }}>
                {direction}
              </span>
              {isElite ? <span style={{ padding: "5px 9px", borderRadius: 999, border: `1px solid ${palette.elite}`, color: palette.elite, fontWeight: 950, fontSize: 11 }}>ELITE</span> : null}
              {privateSetup ? (
                <button
                  type="button"
                  aria-expanded={isSetupExpanded}
                  onClick={() => setExpandedSetupCards((prev) => ({ ...prev, [cardKey]: !prev[cardKey] }))}
                  title="Private owner setup-fit classification"
                  style={{ padding: "5px 10px", borderRadius: 999, border: `1px solid ${privateSetup.color}`, background: `${privateSetup.color}18`, color: privateSetup.color, fontWeight: 950, fontSize: 11, cursor: "pointer" }}
                >
                  {privateSetup.label}{privateSetup.score !== null ? ` · ${privateSetup.score}` : ""}
                </button>
              ) : null}
            </div>
            <div style={{ color: palette.muted, marginTop: 5, fontSize: 13, lineHeight: 1.25, overflowWrap: "anywhere" }}>{company || "—"}</div>
            <div style={{ color: palette.muted, marginTop: 7, fontSize: 12 }}>
              <span style={{ fontWeight: 850, color: palette.text }}>{day || "—"}</span> · {reportTime ? `${reportTime}-market` : "—"} · as of {r?.as_of || "—"}
            </div>
          </div>

          <div style={{ textAlign: "right", minWidth: 98, flex: "0 0 auto" }}>
            <div style={{ fontSize: 21, fontWeight: 950 }}>{fmtMoney(price)}</div>
            <div style={{ marginTop: 7, display: "grid", gap: 4, justifyItems: "end" }}>
              <span style={{ fontSize: 11, color: palette.muted, fontWeight: 800 }}>1M {fmtPct(pct1m)}</span>
              <span style={{ fontSize: 11, color: palette.muted, fontWeight: 800 }}>3M {fmtPct(pct3m)}</span>
              <span style={{ fontSize: 11, color: palette.muted, fontWeight: 800 }}>YTD {fmtPct(pctytd)}</span>
            </div>
          </div>
        </div>

        {privateSetup && isSetupExpanded ? (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 14, border: `1px solid ${privateSetup.color}55`, background: `${privateSetup.color}0d` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, color: palette.muted, fontWeight: 950, letterSpacing: 0.8, textTransform: "uppercase" }}>Private Setup Match</div>
                <div style={{ marginTop: 3, fontWeight: 950, color: privateSetup.color }}>{privateSetup.label}</div>
              </div>
              <div style={{ textAlign: "right", fontSize: 12, fontWeight: 900 }}>
                <div>{privateSetup.direction}</div>
                <div style={{ color: palette.muted }}>Score {privateSetup.score ?? "—"}</div>
              </div>
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 8 }}>
              {[
                ["3D 50MA", fmtPct(r?.["3d_delta_from_ma50_pct"] ?? r?.ma3d_delta_from_ma50)],
                ["3D Momentum", compactLabel(r?.TEG_StochRSI_3D_State || "—")],
                ["Weekly Momentum", compactLabel(r?.TEG_StochRSI_1W_State || "—")],
                ["3D Room", fmtPct(r?.["3d_bb_room_up_pct"])],
                ["Weekly Room", fmtPct(r?.["1w_bb_room_up_pct"])],
                ["Structure", compactLabel(r?.TEG_Structure_Gate || "—")],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: 8, borderRadius: 10, background: palette.card, border: `1px solid ${palette.border}` }}>
                  <div style={{ fontSize: 9, color: palette.muted, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
                  <div style={{ marginTop: 2, fontSize: 12, fontWeight: 900 }}>{value}</div>
                </div>
              ))}
            </div>
            {privateSetup.matched.length ? <div style={{ marginTop: 9, fontSize: 12 }}><strong>Why it matches:</strong> {privateSetup.matched.map(compactLabel).join(" · ")}</div> : null}
            {privateSetup.missing.length ? <div style={{ marginTop: 7, fontSize: 12, color: palette.watch }}><strong>Missing:</strong> {privateSetup.missing.map(compactLabel).join(" · ")}</div> : null}
            {privateSetup.warnings.length ? <div style={{ marginTop: 7, fontSize: 12, color: palette.put }}><strong>Conflicts:</strong> {privateSetup.warnings.map(compactLabel).join(" · ")}</div> : null}
          </div>
        ) : null}

        <div style={{ marginTop: 14, padding: "13px 14px", borderRadius: 14, background: directionMeta.soft, border: `1px solid ${directionMeta.color}33` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div className="teg-editorial" style={{ fontSize: 10, color: palette.muted, fontWeight: 950, letterSpacing: 0.9, textTransform: "uppercase" }}>Guru Decision</div>
              <div className="teg-editorial" style={{ fontSize: 17, fontWeight: 950, marginTop: 2 }}>{execution.label}</div>
              <div style={{ color: palette.muted, fontSize: 12, marginTop: 2 }}>{execution.detail}</div>
            </div>
            <div style={{ padding: "8px 12px", borderRadius: 12, background: execution.color, color: "#fff", fontSize: 12, fontWeight: 950, letterSpacing: 0.4 }}>
              {execution.label}
            </div>
          </div>
        </div>

        {tickerGuruPlay ? <div key={tickerGuruPlay.id} style={{marginTop:12}}><GuruPlayMakerResult result={tickerGuruPlay} onView={adminMode ? () => { setEditingGuruPlay(tickerGuruPlay); setPlayMakerTicker(ticker); } : undefined} /></div> : null}
        {canOpenPlayMaker ? <button type="button" onClick={() => { setEditingGuruPlay(null); setPlayMakerTicker(playMakerTicker === ticker ? null : ticker); }} style={{marginTop:12}}>OPEN GURU PLAYMAKER</button> : null}
        {playMakerTicker === ticker && canOpenPlayMaker ? <div style={{marginTop:12}}><GuruPlayMakerEditor key={editingGuruPlay?.id || `new-${ticker}`} play={editingGuruPlay} request={{ source:"TEG", ticker, direction:m.sig === "BEARISH" ? "PUT" : "CALL", underlying_price:Number(price), setup_type:m.setup, confidence:Number(m.confidenceScore || 0), trigger:m.entry, target:r?.TEG_3D_Squeeze_Target || null, invalidation:r?.TEG_Invalidation || null, attack_levels:[], source_metadata:{source_snapshot:tegSourceSnapshot(r,m,privateSetup)} }} onSaved={refreshGuruPlays} onClose={() => { setPlayMakerTicker(null); setEditingGuruPlay(null); }} /></div> : null}

        <div className="teg-card-thesis" style={{ marginTop: 12 }}>
          <div className="teg-editorial" style={{ fontSize: 10, color: palette.muted, fontWeight: 950, letterSpacing: 0.9, textTransform: "uppercase" }}>Guru Thesis</div>
          <div className="teg-editorial teg-card-clamp teg-card-clamp-2" style={{ fontSize: 15, fontWeight: 900, lineHeight: 1.38, marginTop: 4 }}>{m.eliteHeadline}</div>
          {m.eliteSubtext ? <div className="teg-card-clamp teg-card-clamp-1" style={{ color: palette.muted, fontSize: 12, marginTop: 4 }}>{m.eliteSubtext}</div> : null}
        </div>

        <div className="teg-card-summary-grid" style={{ marginTop: 13, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
          {[
            ["PLAY", m.playType || "—"],
            ["ENTRY", execution.detail],
            ["RISK", m.riskSummary || "—"],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: "10px", borderRadius: 12, background: palette.soft, border: `1px solid ${palette.border}`, minWidth: 0 }}>
              <div style={{ fontSize: 9, color: palette.muted, fontWeight: 950, letterSpacing: 0.8 }}>{label}</div>
              <div className={`teg-card-field-copy teg-card-clamp teg-card-clamp-${label === "RISK" ? "4" : "2"}`} style={{ marginTop: 3, fontSize: 12, fontWeight: 900, overflowWrap: "anywhere" }}>{value}</div>
            </div>
          ))}
        </div>

        <div className="teg-card-action-row" style={{ marginTop: 12, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div className="teg-card-targets" style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {chip(m.confidenceLayer || "Conviction pending", isElite ? "bull" : "neutral", { maxWidth: 165 })}
            {m.targetSummary ? chip(m.targetSummary, "neutral", { maxWidth: 210 }) : null}
          </div>
          {link ? <a href={link} target="_blank" rel="noreferrer" style={{ color: directionMeta.color, fontWeight: 950, textDecoration: "none", fontSize: 13 }}>Open Chart ↗</a> : null}
        </div>

        {adminMode ? (
          <>
            <button
              type="button"
              className={`teg-intelligence-toggle ${isExpanded ? "is-expanded" : ""}`}
              aria-expanded={isExpanded}
              onClick={() => setExpandedCards((prev) => ({ ...prev, [cardKey]: !prev[cardKey] }))}
            >
              {isExpanded ? "− Hide Intelligence" : "Expand Intelligence"}
            </button>

            {isExpanded ? (
              <div style={{ marginTop: 12, borderTop: `1px dashed ${palette.border}`, paddingTop: 12 }}>
                <details open style={{ marginBottom: 9 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 950, padding: "9px 0" }}>Decision trace</summary>
                  {m.reason ? sectionBox(<><strong>Full Why:</strong> {m.reason}</>, { fontSize: 13, marginTop: 0 }) : null}
                </details>

                <details style={{ marginBottom: 9 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 950, padding: "9px 0" }}>Multi-timeframe structure</summary>
                  {sectionBox(
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 10 }}>
                      {[
                        ["Daily", m.daily20, m.daily50, m.daily200, m.daily50Delta],
                        ["3-Day", m.ma3d20, m.ma3d50, m.ma3d200, m.ma3d50Delta],
                        ["Weekly", m.ma1w20, m.ma1w50, m.ma1w200, m.ma1w50Delta],
                      ].map(([label, ma20, ma50, ma200, delta]) => (
                        <div key={label} style={{ padding: 10, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.card }}>
                          <div style={{ fontWeight: 950, marginBottom: 7 }}>{label}</div>
                          <div>20MA: <strong>{fmtMoney(ma20)}</strong></div>
                          <div>50MA: <strong>{fmtMoney(ma50)}</strong></div>
                          <div>200MA: <strong>{fmtMoney(ma200)}</strong></div>
                          <div style={{ marginTop: 5, color: safeNum(delta) !== null && safeNum(delta) >= 0 ? palette.call : palette.put, fontWeight: 900 }}>vs 50MA: {fmtPct(delta)}</div>
                        </div>
                      ))}
                    </div>, { marginTop: 0 }
                  )}
                </details>

                <details style={{ marginBottom: 9 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 950, padding: "9px 0" }}>Momentum timing</summary>
                  {sectionBox(
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 10 }}>
                      {[
                        ["Daily", m.stoch1dK, m.stoch1dD, m.stoch1dState],
                        ["3-Day", m.stoch3dK, m.stoch3dD, m.stoch3dState],
                        ["Weekly", m.stoch1wK, m.stoch1wD, m.stoch1wState],
                      ].map(([label, k, d, state]) => (
                        <div key={label} style={{ padding: 10, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.card }}>
                          <div style={{ fontWeight: 950, marginBottom: 7 }}>{label}</div>
                          <div>K / D: <strong>{fmtNum(k)} / {fmtNum(d)}</strong></div>
                          <div style={{ marginTop: 5 }}>{chip(state, stochTone(state), { maxWidth: 145 })}</div>
                        </div>
                      ))}
                    </div>, { marginTop: 0 }
                  )}
                </details>

                <details style={{ marginBottom: 9 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 950, padding: "9px 0" }}>Runway, revisions and risk</summary>
                  {sectionBox(
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
                      {[
                        ["3D BB upside", fmtPct(m.bbUp)], ["3D BB downside", fmtPct(m.bbDn)],
                        ["Weekly upside", fmtPct(m.wBbUp)], ["Weekly downside", fmtPct(m.wBbDn)],
                        ["Revision", m.revisionContext], ["Divergence", m.divergence],
                        ["Exhaustion", m.exhaustionRisk], ["Timeframe divergence", m.timeframeDivergence],
                      ].map(([label, value]) => (
                        <div key={label} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: palette.muted, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
                          <div style={{ fontWeight: 850, overflowWrap: "anywhere" }}>{compactLabel(value || "—")}</div>
                        </div>
                      ))}
                    </div>, { marginTop: 0 }
                  )}
                </details>

                <details>
                  <summary style={{ cursor: "pointer", fontWeight: 950, padding: "9px 0" }}>Raw engine output</summary>
                  {sectionBox(
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                      {[
                        ["Raw signal", m.sig], ["Raw option", m.opt], ["Entry rule", m.entry],
                        ["Condition", m.conditionLabel || m.condition], ["Market state", m.marketStateLabel || m.marketState],
                        ["Setup", m.setup], ["Confidence", m.confidenceLayer], ["Confidence score", fmtNum(m.confidenceScore)],
                      ].map(([label, value]) => (
                        <div key={label} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: palette.muted, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
                          <div style={{ fontWeight: 850, overflowWrap: "anywhere" }}>{compactLabel(value || "—")}</div>
                        </div>
                      ))}
                    </div>, { marginTop: 0 }
                  )}
                </details>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  const tableHeaderCell = (label, key, width) => (
    <th
      style={{
        textAlign: "left",
        padding: "10px 10px",
        borderBottom: `1px solid ${palette.border}`,
        color: palette.muted,
        fontSize: 12,
        fontWeight: 900,
        letterSpacing: 0.2,
        cursor: key ? "pointer" : "default",
        width: width || "auto",
        whiteSpace: "nowrap",
      }}
      onClick={key ? () => toggleSort(key) : undefined}
      title={key ? `Sort by ${key}` : ""}
    >
      {label} {key && sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  return (
    <div
      className="teg-dashboard"
      data-theme={tegTheme}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        padding: 16,
        background: palette.bg,
        color: palette.text,
        borderRadius: 22,
      }}
    >
      <div className="teg-shell" style={{ display: "grid", gridTemplateColumns: "minmax(220px, 250px) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
        <aside className="teg-calendar teg-desktop-calendar" style={{ position: "sticky", top: 12, border: `1px solid ${palette.border}`, background: palette.card, borderRadius: 18, padding: 12, maxHeight: "calc(100vh - 28px)", overflowY: "auto" }}>
          <div style={{ fontSize: 11, color: palette.muted, fontWeight: 950, letterSpacing: 0.9, textTransform: "uppercase" }}>Earnings Calendar</div>
          <div className="teg-editorial" style={{ fontSize: 17, fontWeight: 950, marginTop: 3 }}>Upcoming Reports</div>
          <button className={`teg-calendar-button ${calendarDate === "ALL" ? "is-selected" : ""}`} type="button" onClick={() => setCalendarDate("ALL")} style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 12, textAlign: "left", cursor: "pointer", border: `1px solid ${calendarDate === "ALL" ? palette.accent : palette.border}`, background: calendarDate === "ALL" ? "rgba(14,165,233,.16)" : palette.soft, color: palette.text, fontWeight: 900 }}>All dates <span style={{ float: "right" }}>{scanCounts.total}</span></button>
          <div style={{ display: "grid", gap: 8, marginTop: 9 }}>
            {calendarGroups.map((g) => (
              <button className={`teg-calendar-button ${calendarDate === g.date ? "is-selected" : ""}`} key={g.date} type="button" onClick={() => setCalendarDate(g.date)} style={{ width: "100%", padding: "10px", borderRadius: 12, textAlign: "left", cursor: "pointer", border: `1px solid ${calendarDate === g.date ? palette.accent : palette.border}`, background: calendarDate === g.date ? "rgba(14,165,233,.16)" : palette.card, color: palette.text }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontWeight: 950 }}><span>{formatCalendarDate(g.date)}</span><span>{g.total}</span></div>
                <div style={{ marginTop: 5, display: "flex", gap: 6, flexWrap: "wrap", color: palette.muted, fontSize: 10, fontWeight: 850 }}>
                  <span>PRE {g.pre}</span><span>POST {g.post}</span><span style={{ color: palette.call }}>C {g.calls}</span><span style={{ color: palette.put }}>P {g.puts}</span>
                </div>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${palette.border}` }}>
            <div style={{ fontSize: 10, color: palette.muted, fontWeight: 950, textTransform: "uppercase" }}>Selected</div>
            <div style={{ marginTop: 3, fontWeight: 900 }}>{calendarDate === "ALL" ? "All earnings dates" : formatCalendarDate(calendarDate)}</div>
          </div>
        </aside>

        <main style={{ minWidth: 0 }}>
          <div className="teg-mobile-header" style={{ display: "none" }}>
            <button className="teg-mobile-scope" type="button" onClick={() => setMobileFiltersOpen(true)} aria-label="Choose earnings date" style={{ minWidth: 0 }}>
              <div className="teg-editorial" style={{ fontSize: 19, fontWeight: 950, color: palette.text }}>The Earnings Guru</div>
              <div style={{ marginTop: 2, fontSize: 12, color: palette.muted }}>
                {calendarDate === "ALL" ? "All upcoming earnings" : formatCalendarDate(calendarDate)} · {sorted.length} tickers
              </div>
            </button>
            <div className="teg-mobile-header-actions">
              {themeToggle(true)}
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                aria-label="Open TEG filters"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  flex: "0 0 auto",
                  padding: "10px 14px",
                  borderRadius: 14,
                  border: `1px solid ${palette.border}`,
                  background: palette.accent,
                  color: "#fff",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                <span aria-hidden="true">☰</span> Filters
              </button>
            </div>
          </div>

          <div className="teg-desktop-theme-row">
            {themeToggle()}
          </div>

          {mobileFiltersOpen ? (
            <button
              className="teg-mobile-backdrop"
              type="button"
              aria-label="Close filters"
              onClick={() => setMobileFiltersOpen(false)}
            />
          ) : null}

      <div
        className={`teg-filter-panel ${mobileFiltersOpen ? "is-open" : ""}`}
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          border: `1px solid ${palette.border}`,
          background: palette.workspace,
          borderRadius: 18,
          padding: 14,
        }}
      >
        <div className="teg-mobile-drawer-head" style={{ display: "none" }}>
          <div>
            <div style={{ fontSize: 11, color: palette.muted, fontWeight: 950, letterSpacing: 0.8, textTransform: "uppercase" }}>TEG Controls</div>
            <div style={{ marginTop: 2, fontSize: 19, fontWeight: 950, color: palette.text }}>Calendar & Filters</div>
          </div>
          <button type="button" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters" style={{ width: 40, height: 40, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, fontSize: 20, fontWeight: 900, cursor: "pointer" }}>×</button>
        </div>

        <div className="teg-mobile-calendar-select" style={{ display: "none", marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Earnings date</div>
          <select value={calendarDate} onChange={(e) => setCalendarDate(e.target.value)} style={{ width: "100%", padding: "11px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 850 }}>
            <option value="ALL">All dates ({scanCounts.total})</option>
            {calendarGroups.map((g) => (
              <option key={g.date} value={g.date}>{formatCalendarDate(g.date)} · {g.total} reports · C {g.calls} / P {g.puts}</option>
            ))}
          </select>
        </div>

        <div className="teg-filter-title-row" style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="teg-editorial" style={{ fontSize: 18, fontWeight: 950, color: palette.text, letterSpacing: 0.2 }}>The Earnings Guru</div>
            <div style={{ fontSize: 12, color: palette.muted }}>Earnings continuation and reversal intelligence — distilled into a clear decision.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
            <div className="teg-view-toggle" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => setView("cards")} style={{ padding: "10px 14px", borderRadius: 999, border: `1px solid ${palette.border}`, background: view === "cards" ? palette.accent : "transparent", color: view === "cards" ? "#fff" : palette.text, fontWeight: 900, cursor: "pointer" }}>Cards</button>
              <button type="button" onClick={() => setView("table")} style={{ padding: "10px 14px", borderRadius: 999, border: `1px solid ${palette.border}`, background: view === "table" ? palette.accent : "transparent", color: view === "table" ? "#fff" : palette.text, fontWeight: 900, cursor: "pointer" }}>Table</button>
            </div>
            <div style={{ whiteSpace: "nowrap", color: palette.muted, fontSize: 12, fontWeight: 900 }}>{sorted.length} tickers</div>
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: palette.muted, fontWeight: 950, letterSpacing: 0.8, textTransform: "uppercase" }}>Quick Views</div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {[
            ["CALLS", `Calls (${scanCounts.calls})`],
            ["PUTS", `Puts (${scanCounts.puts})`],
            ["PRE", `Pre (${scanCounts.pre})`],
            ["POST", `Post (${scanCounts.post})`],
            ["HIGH_PROB", `High Prob (${scanCounts.highProb})`],
            ["TRADE_READY", `Trade Ready (${scanCounts.tradeReady})`],
            ["WATCH", "Watch"],
            ["SQUEEZE", `Squeeze (${scanCounts.squeeze})`],
          ].map(([key, label]) => {
            const active =
              (key === "CALLS" && optionFilter === "CALL") ||
              (key === "PUTS" && optionFilter === "PUT") ||
              (key === "PRE" && sessionFilter === "PRE") ||
              (key === "POST" && sessionFilter === "POST") ||
              (key === "HIGH_PROB" && qualityFilter === "HIGH_PROB") ||
              (key === "TRADE_READY" && qualityFilter === "TRADE_READY") ||
              (key === "WATCH" && qualityFilter === "WATCH") ||
              (key === "SQUEEZE" && qualityFilter === "SQUEEZE");
            return (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                className={`teg-quick-view ${active ? "is-active" : ""}`}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: `1px solid ${active ? palette.accent : palette.border}`,
                  background: active ? "rgba(14,165,233,0.24)" : palette.pill,
                  color: palette.text,
                  fontWeight: 950,
                  cursor: "pointer",
                  boxShadow: active && !isTegDark ? "0 6px 16px rgba(14,165,233,0.18)" : "none",
                }}
              >
                {label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={resetScanFilters}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: `1px solid ${palette.border}`,
              background: "transparent",
              color: palette.muted,
              fontWeight: 950,
              cursor: "pointer",
            }}
          >
            Reset Scan
          </button>
        </div>

        {adminMode && setupCounts ? (
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 8 }}>
            {[
              ["Strong Matches", setupCounts.STRONG_MATCH, "#7c3aed"],
              ["Matches", setupCounts.MATCH, "#16a34a"],
              ["Near Matches", setupCounts.NEAR_MATCH, "#d97706"],
              ["Premium Protection", setupCounts.PREMIUM_PROTECTION, "#dc2626"],
            ].map(([label, count, color]) => (
              <div key={label} style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${color}44`, background: `${color}0d` }}>
                <div style={{ fontSize: 10, color: palette.muted, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
                <div style={{ marginTop: 2, color, fontSize: 20, fontWeight: 950 }}>{count}</div>
              </div>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: 14, fontSize: 11, color: palette.muted, fontWeight: 950, letterSpacing: 0.8, textTransform: "uppercase" }}>Filters</div>
        <div className={`teg-filter-row teg-filter-primary ${adminMode ? "is-admin" : ""}`} style={{ marginTop: 8 }}>
          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Signal</div>
            <select value={signalFilter} onChange={(e) => setSignalFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">All Signals</option>
              <option value="BULLISH">Bullish</option>
              <option value="BEARISH">Bearish</option>
              <option value="FLAT">Flat</option>
            </select>
          </div>
          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Options</div>
            <select value={optionFilter} onChange={(e) => setOptionFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">All Options</option>
              <option value="CALL">Calls</option>
              <option value="PUT">Puts</option>
              <option value="SPREAD">Spreads</option>
              <option value="WAIT">Wait</option>
              <option value="NO_TRADE">No Trade</option>
            </select>
          </div>
          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Report Time</div>
            <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">All Times</option>
              <option value="PRE">Pre-market</option>
              <option value="POST">Post-market</option>
            </select>
          </div>
          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Quality</div>
            <select value={qualityFilter} onChange={(e) => setQualityFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">All Quality</option>
              <option value="HIGH_PROB">High Probability</option>
              <option value="TRADE_READY">Trade Ready</option>
              <option value="WATCH">Watch / Wait</option>
              <option value="AVOID">Avoid / No Trade</option>
              <option value="SQUEEZE">Squeeze</option>
            </select>
          </div>
          {adminMode ? (
            <>
              <div className="teg-control">
                <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Private Setup Status</div>
                <select value={setupStatusFilter} onChange={(e) => setSetupStatusFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
                  <option value="ALL">All Setup Statuses</option>
                  <option value="STRONG_MATCH">Strong Setup Match</option>
                  <option value="MATCH">Setup Match</option>
                  <option value="NEAR_MATCH">Near Match</option>
                  <option value="PREMIUM_PROTECTION">Premium Protection</option>
                  <option value="NO_MATCH">No Match</option>
                </select>
              </div>
              <div className="teg-control">
                <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Private Setup Direction</div>
                <select value={setupDirectionFilter} onChange={(e) => setSetupDirectionFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
                  <option value="ALL">All Directions</option>
                  <option value="BULLISH">Bullish Setup Only</option>
                  <option value="BEARISH">Bearish Setup Only</option>
                  <option value="NEUTRAL">Neutral</option>
                </select>
              </div>
            </>
          ) : null}
        </div>

        <div className="teg-filter-row teg-filter-secondary" style={{ marginTop: 12 }}>
          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Search ticker / company</div>
            <input value={playerInput} onChange={(e) => setPlayerInput(e.target.value)} placeholder="Type ticker or company..." style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 700 }} />
          </div>

          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Sector</div>
            <select value={selectedSector || "ALL"} onChange={(e) => setSelectedSector(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">ALL</option>
              {(uniqueSectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Structure</div>
            <select value={structureFilter} onChange={(e) => setStructureFilter(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="ALL">All structure</option>
              <option value="3D_ABOVE_50">Above 3D 50MA</option>
              <option value="3D_BELOW_50">Below 3D 50MA</option>
              <option value="3D_RECLAIM_TARGET">3D 50MA reclaim candidates</option>
              <option value="WEEKLY_ABOVE_50">Above weekly 50MA</option>
              <option value="WEEKLY_BELOW_50">Below weekly 50MA</option>
              <option value="ALIGNED_ABOVE">Above 3D + weekly 50MA</option>
              <option value="ALIGNED_BELOW">Below 3D + weekly 50MA</option>
            </select>
          </div>

          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Price</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
              <input type="number" inputMode="decimal" step="any" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min $" aria-label="Minimum Price" style={{ width: "100%", minWidth: 0, padding: "10px 9px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }} />
              <input type="number" inputMode="decimal" step="any" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max $" aria-label="Maximum Price" style={{ width: "100%", minWidth: 0, padding: "10px 9px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }} />
            </div>
          </div>

          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Sort by</div>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="day">Day</option>
              <option value="report_time">Report time</option>
              <option value="TEG_Signal">Signal</option>
              <option value="TEG_Score">Score</option>
              <option value="TEG_Score_Total">Score Total</option>
              <option value="TEG_Entry_Timing">Entry Timing</option>
              <option value="TEG_StochRSI_3D_State">3D Stoch RSI</option>
              <option value="TEG_Condition">Condition</option>
              <option value="TEG_Confidence_Score">Confidence Score</option>
              <option value="TEG_Momentum_Exception">Momentum Exception</option>
              <option value="TEG_Divergence">Divergence</option>
              <option value="TEG_Exhaustion_Risk">Exhaustion</option>
              <option value="pct_1m">1M %</option>
              <option value="pct_3m">3M %</option>
              <option value="pct_ytd">YTD %</option>
            </select>
          </div>

          <div className="teg-control">
            <div style={{ fontSize: 12, fontWeight: 900, color: palette.muted, marginBottom: 6 }}>Order by</div>
            <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 14, border: `1px solid ${palette.border}`, background: palette.soft, color: palette.text, outline: "none", fontWeight: 800 }}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
        </div>

        <button
          className="teg-mobile-apply"
          type="button"
          onClick={() => setMobileFiltersOpen(false)}
          style={{ display: "none" }}
        >
          Show {sorted.length} cards
        </button>
      </div>

      {(isMobile || view === "cards") ? (
        <div className="teg-results-grid" style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 14, alignItems: "start" }}>
          {sorted.map(card)}
          {sorted.length === 0 ? <div style={{ padding: 18, color: palette.muted, fontWeight: 800 }}>No tickers match your filters.</div> : null}
        </div>
      ) : (
        <div style={{ marginTop: 14, border: `1px solid ${palette.border}`, borderRadius: 16, overflow: "hidden", background: palette.bg }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {tableHeaderCell("Day", "day", 92)}
                  {tableHeaderCell("Ticker", "ticker", 96)}
                  {adminMode ? tableHeaderCell("Setup Match", "setup_match_status", 160) : null}
                  {tableHeaderCell("Company", "company_earnings", 260)}
                  {tableHeaderCell("Report", "report_time", 90)}
                  {tableHeaderCell("Signal", "TEG_Signal", 120)}
                  {tableHeaderCell("Setup", "TEG_Setup", 150)}
                  {tableHeaderCell("Score", "TEG_Score", 80)}
                  {tableHeaderCell("1M%", "pct_1m", 70)}
                  {tableHeaderCell("3M%", "pct_3m", 70)}
                  {tableHeaderCell("YTD%", "pct_ytd", 70)}
                  {tableHeaderCell("Entry", "Entry_Timing", 140)}
                  {tableHeaderCell("3D Stoch", "TEG_StochRSI_3D_State", 140)}
                  {tableHeaderCell("Momentum", "TEG_Momentum_Exception", 150)}
                  {tableHeaderCell("Divergence", "TEG_Divergence", 150)}
                  {tableHeaderCell("Exhaustion", "TEG_Exhaustion_Risk", 150)}
                  {tableHeaderCell("Options", "Option_Strategy", 150)}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const ticker = r?.ticker;
                  const link = tvUrl(ticker);
                  const stochState = r?.TEG_StochRSI_3D_State || stochRsiStateFallback(r?.tf3d_stoch_rsi_k, r?.tf3d_stoch_rsi_d);
                  return (
                    <tr key={`${ticker}-${i}`} style={{ borderBottom: `1px solid ${palette.border}` }}>
                      <td style={{ padding: "10px", fontWeight: 900, color: palette.text, whiteSpace: "nowrap" }}>{r?.day || "—"}</td>
                      <td style={{ padding: "10px", fontWeight: 950, color: palette.text, whiteSpace: "nowrap" }}>{ticker || "—"}{link ? <a href={link} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: "#2563eb", fontWeight: 900, textDecoration: "none" }}>↗</a> : null}</td>
                      {adminMode ? <td style={{ padding: "10px", whiteSpace: "nowrap" }}>{chip(getPrivateTegSetup(r, true)?.label || "—", "neutral", { maxWidth: 170 })}</td> : null}
                      <td style={{ padding: "10px", color: palette.text, minWidth: 240 }}>{r?.company_earnings || r?.company || "—"}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{r?.report_time ? `${r.report_time}-market` : "—"}</td>
                      <td style={{ padding: "10px" }}>{chip(r?.TEG_Signal || "—", signalTone(r?.TEG_Signal), { maxWidth: 120 })}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(r?.TEG_Setup || "—")}</td>
                      <td style={{ padding: "10px", color: palette.text, fontWeight: 900, whiteSpace: "nowrap" }}>{r?.TEG_Score ?? "—"}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{fmtPct(r?.pct_1m)}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{fmtPct(r?.pct_3m)}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{fmtPct(r?.pct_ytd)}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(r?.Entry_Timing || r?.TEG_Entry_Timing || "—")}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(stochState)}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(r?.TEG_Momentum_Exception || "—")}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(r?.TEG_Divergence || "—")}</td>
                      <td style={{ padding: "10px", color: palette.text, whiteSpace: "nowrap" }}>{compactLabel(r?.TEG_Exhaustion_Risk || "—")}</td>
                      <td style={{ padding: "10px", color: palette.text, minWidth: 140 }}>{compactLabel(r?.Option_Strategy || "—")}</td>
                    </tr>
                  );
                })}
                {sorted.length === 0 ? <tr><td colSpan={17} style={{ padding: 16, color: palette.muted, fontWeight: 800 }}>No tickers match your filters.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </main>
      </div>

      <style>{`
        .teg-dashboard {
          --teg-page: #08111f;
          --teg-workspace: #0e1726;
          --teg-card: #131e2e;
          --teg-control: #182334;
          --teg-border: #2a394d;
          --teg-text: #f4f7fb;
          --teg-muted: #94a3b8;
          --teg-accent: #0ea5e9;
          --teg-page-glow: rgba(14,165,233,.10);
          --teg-panel-top: #111d2d;
          --teg-card-top: #152235;
          --teg-hover: #17263a;
          --teg-hover-border: #46617f;
          --teg-row-hover: rgba(30,49,73,.56);
          --teg-shadow: 0 18px 42px rgba(0,0,0,.22);
          --teg-card-shadow: 0 14px 34px rgba(0,0,0,.24);
          --teg-card-hover-shadow: 0 18px 42px rgba(0,0,0,.30);
          --teg-mobile-backdrop: rgba(15,23,42,.48);
          --teg-intelligence: #101c2d;
          --teg-intelligence-hover: #14314a;
          --teg-intelligence-expanded: #123653;
          --teg-intelligence-expanded-hover: #164665;
          --teg-intelligence-text: #7dd3fc;
          --teg-intelligence-active-text: #f4f7fb;
          color-scheme: dark;
          min-height: min(100vh, 1080px);
          background:
            radial-gradient(circle at 72% -10%, var(--teg-page-glow), transparent 34%),
            var(--teg-page) !important;
        }
        .teg-dashboard[data-theme="light"] {
          --teg-page: #f5f7fb;
          --teg-workspace: #ffffff;
          --teg-card: #ffffff;
          --teg-control: #f8fafc;
          --teg-border: #d7dee8;
          --teg-text: #101828;
          --teg-muted: #667085;
          --teg-page-glow: rgba(14,165,233,.08);
          --teg-panel-top: #ffffff;
          --teg-card-top: #ffffff;
          --teg-hover: #eef6fc;
          --teg-hover-border: #7cc8ee;
          --teg-row-hover: #f1f7fc;
          --teg-shadow: 0 16px 38px rgba(15,23,42,.09);
          --teg-card-shadow: 0 12px 28px rgba(15,23,42,.10);
          --teg-card-hover-shadow: 0 16px 34px rgba(15,23,42,.14);
          --teg-mobile-backdrop: rgba(15,23,42,.34);
          --teg-intelligence: #eaf6fd;
          --teg-intelligence-hover: #d9effb;
          --teg-intelligence-expanded: #d7eefb;
          --teg-intelligence-expanded-hover: #c4e6f8;
          --teg-intelligence-text: #0369a1;
          --teg-intelligence-active-text: #0c4a6e;
          color-scheme: light;
        }
        .teg-dashboard *, .teg-dashboard *::before, .teg-dashboard *::after { box-sizing: border-box; }
        .teg-editorial { font-family: Georgia, "Times New Roman", serif; }
        .teg-calendar {
          background: linear-gradient(180deg, var(--teg-panel-top) 0%, var(--teg-workspace) 100%) !important;
          box-shadow: var(--teg-shadow) !important;
          scrollbar-color: #3b4f68 transparent;
        }
        .teg-calendar-button {
          transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
        }
        .teg-calendar-button:hover { border-color: var(--teg-hover-border) !important; background: var(--teg-hover) !important; }
        .teg-calendar-button.is-selected {
          border-color: var(--teg-accent) !important;
          background: rgba(14,165,233,.16) !important;
          box-shadow: inset 3px 0 0 var(--teg-accent);
        }
        .teg-filter-panel {
          background: linear-gradient(180deg, var(--teg-panel-top), var(--teg-workspace)) !important;
          box-shadow: var(--teg-shadow) !important;
        }
        .teg-filter-row {
          display: grid;
          gap: 10px;
          align-items: end;
          width: 100%;
          min-width: 0;
        }
        .teg-filter-primary { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .teg-filter-primary.is-admin { grid-template-columns: repeat(6, minmax(0, 1fr)); }
        .teg-filter-secondary {
          grid-template-columns:
            minmax(180px, 1.55fr)
            minmax(90px, .72fr)
            minmax(130px, 1fr)
            minmax(150px, .95fr)
            minmax(120px, .82fr)
            minmax(90px, .62fr);
        }
        .teg-control { min-width: 0; }
        .teg-control > div:first-child {
          min-height: 17px;
          line-height: 1.15;
        }
        .teg-dashboard select,
        .teg-dashboard input {
          min-width: 0;
          height: 44px;
          border-color: var(--teg-border) !important;
          background: var(--teg-control) !important;
          color: var(--teg-text) !important;
          border-radius: 12px !important;
          transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
        }
        .teg-dashboard select:hover,
        .teg-dashboard input:hover { border-color: var(--teg-hover-border) !important; }
        .teg-dashboard button:focus-visible,
        .teg-dashboard a:focus-visible,
        .teg-dashboard select:focus-visible,
        .teg-dashboard input:focus-visible {
          outline: 2px solid var(--teg-accent) !important;
          outline-offset: 2px;
          box-shadow: 0 0 0 3px rgba(14,165,233,.18);
        }
        .teg-theme-toggle {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 3px;
          border: 1px solid var(--teg-border);
          border-radius: 999px;
          background: var(--teg-control);
        }
        .teg-theme-toggle button {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 32px;
          padding: 6px 9px;
          border: 0;
          border-radius: 999px;
          background: transparent;
          color: var(--teg-muted);
          font: inherit;
          font-size: 11px;
          font-weight: 900;
          cursor: pointer;
        }
        .teg-theme-toggle button:hover { background: var(--teg-hover); color: var(--teg-text); }
        .teg-theme-toggle button.is-active {
          background: var(--teg-accent);
          color: #ffffff;
          box-shadow: 0 5px 14px rgba(14,165,233,.20);
        }
        .teg-mobile-header-actions { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
        .teg-desktop-theme-row { display: flex; justify-content: flex-end; margin-bottom: 10px; }
        .teg-quick-view {
          min-height: 38px;
          transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
        }
        .teg-quick-view:hover { border-color: var(--teg-hover-border) !important; transform: translateY(-1px); }
        .teg-quick-view.is-active {
          border-color: var(--teg-accent) !important;
          background: rgba(14,165,233,.24) !important;
          box-shadow: 0 8px 20px rgba(14,165,233,.13) !important;
        }
        .teg-dashboard .teg-result-card button.teg-intelligence-toggle {
          appearance: none;
          -webkit-appearance: none;
          margin-top: 12px;
          padding: 9px 13px;
          border: 1px solid var(--teg-accent);
          border-radius: 999px;
          background-color: var(--teg-intelligence);
          color: var(--teg-intelligence-text);
          -webkit-text-fill-color: currentColor;
          opacity: 1;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          transition: color 140ms ease, background 140ms ease, box-shadow 140ms ease, transform 140ms ease;
        }
        .teg-dashboard .teg-result-card button.teg-intelligence-toggle:hover {
          background-color: var(--teg-intelligence-hover);
          color: var(--teg-intelligence-active-text);
          box-shadow: 0 0 0 3px rgba(14,165,233,.12), 0 8px 20px rgba(14,165,233,.12);
          transform: translateY(-1px);
        }
        .teg-dashboard .teg-result-card button.teg-intelligence-toggle[aria-expanded="true"] {
          background-color: var(--teg-intelligence-expanded);
          color: var(--teg-intelligence-active-text);
          box-shadow: inset 0 0 0 1px rgba(125,211,252,.08);
        }
        .teg-dashboard .teg-result-card button.teg-intelligence-toggle[aria-expanded="true"]:hover {
          background-color: var(--teg-intelligence-expanded-hover);
        }
        .teg-results-grid {
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr)) !important;
          align-items: stretch !important;
        }
        .teg-result-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: linear-gradient(180deg, var(--teg-card-top) 0%, var(--teg-card) 100%) !important;
          border-color: var(--teg-border) !important;
          box-shadow: var(--teg-card-shadow) !important;
          transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
        }
        .teg-result-card:hover {
          transform: translateY(-2px);
          border-color: var(--teg-hover-border) !important;
          box-shadow: var(--teg-card-hover-shadow) !important;
        }
        .teg-results-grid:has(.teg-result-card.is-expanded) { align-items: start !important; }
        .teg-results-grid:has(.teg-result-card.is-expanded) .teg-result-card,
        .teg-result-card.is-expanded { height: auto; }
        .teg-card-clamp {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .teg-card-clamp-1 { -webkit-line-clamp: 1; }
        .teg-card-clamp-2 { -webkit-line-clamp: 2; }
        .teg-card-clamp-4 { -webkit-line-clamp: 4; }
        .teg-result-card.is-expanded .teg-card-clamp {
          display: block;
          overflow: visible;
          text-overflow: clip;
        }
        .teg-card-summary-grid > div { display: flex; flex-direction: column; }
        .teg-card-field-copy { min-width: 0; }
        .teg-card-action-row { margin-top: auto !important; padding-top: 12px; }
        .teg-card-targets { min-width: 0; overflow: hidden; }
        .teg-dashboard table { background: var(--teg-workspace); }
        .teg-dashboard thead { background: var(--teg-panel-top); }
        .teg-dashboard tbody tr:hover { background: var(--teg-row-hover); }
        .teg-mobile-scope {
          appearance: none;
          padding: 0;
          border: 0;
          background: transparent;
          color: inherit;
          text-align: left;
          cursor: pointer;
        }
        .teg-mobile-backdrop { display: none; }
        @media (min-width: 901px) and (max-width: 1180px) {
          .teg-filter-primary.is-admin { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .teg-filter-secondary { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 900px) {
          .teg-dashboard { padding: 10px !important; border-radius: 16px !important; min-height: 100vh; }
          .teg-shell { grid-template-columns: 1fr !important; }
          .teg-desktop-calendar { display: none !important; }
          .teg-desktop-theme-row { display: none; }
          .teg-mobile-header {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 12px;
            padding: 12px 13px;
            border: 1px solid ${palette.border};
            border-radius: 16px;
            background: linear-gradient(180deg, var(--teg-card-top), var(--teg-workspace));
            box-shadow: var(--teg-card-shadow);
          }
          .teg-mobile-backdrop {
            display: block;
            position: fixed;
            inset: 0;
            z-index: 1998;
            border: 0;
            padding: 0;
            background: var(--teg-mobile-backdrop);
            backdrop-filter: blur(2px);
          }
          .teg-filter-panel {
            position: fixed !important;
            top: 0;
            left: 0;
            z-index: 1999;
            width: min(90vw, 410px) !important;
            height: 100dvh;
            max-height: 100dvh;
            overflow-y: auto !important;
            border-radius: 0 20px 20px 0 !important;
            padding: 16px !important;
            transform: translateX(-105%);
            transition: transform 180ms ease;
            box-shadow: var(--teg-shadow) !important;
          }
          .teg-filter-row,
          .teg-filter-primary,
          .teg-filter-primary.is-admin,
          .teg-filter-secondary { grid-template-columns: minmax(0, 1fr) !important; }
          .teg-filter-panel.is-open { transform: translateX(0); }
          .teg-mobile-drawer-head { display: flex !important; align-items: center; justify-content: space-between; gap: 12px; }
          .teg-mobile-calendar-select { display: block !important; }
          .teg-filter-title-row { display: none !important; }
          .teg-view-toggle { display: none !important; }
          .teg-theme-toggle.is-compact button { min-height: 34px; padding: 6px 8px; }
          .teg-mobile-apply {
            display: block !important;
            position: sticky;
            bottom: 0;
            width: 100%;
            margin-top: 16px;
            padding: 13px 16px;
            border: 0;
            border-radius: 14px;
            background: ${palette.accent};
            color: #fff;
            font-weight: 950;
            font-size: 15px;
            cursor: pointer;
            box-shadow: 0 -8px 20px rgba(15,23,42,.20);
          }
          .teg-results-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 12px !important; align-items: start !important; }
          .teg-result-card { height: auto !important; padding: 14px !important; border-radius: 16px !important; }
          .teg-card-action-row { margin-top: 12px !important; padding-top: 0; }
          .teg-result-card:hover { transform: none; }
        }
      `}</style>
    </div>
  );
}
